import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DPForm } from 'src/app/core/models/DPForm';
import { DPUserForm } from 'src/app/core/models/DPUserForm';
import { CompanyService } from 'src/app/core/services/company.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { DashboardService } from 'src/app/dashboard/services/dashboard.service';
import { DPFormDataResponse } from '../models/DPFormDataResponse';
import { DPJob } from '../models/DPJob';
import { DPTask } from '../models/DPTask';
import { DPFormType } from '../models/enums/DPFormType';
import { DPUserStatus } from '../models/enums/DPUserStatus';
import { ApplicationServiceInterface } from './application.service.interface';
import { AuthService } from './auth.service';

/** Application management service */
@Injectable({
  providedIn: 'root',
})
export class ApplicationService implements ApplicationServiceInterface {
  /** Current applying job object */
  private _job: DPJob | null = null;

  /** Current job forms */
  private _allForms: DPForm[] = [];

  /** Currently showing form */
  private _form: DPForm = null;

  /** Task ID of current form. Available for Dashboard. */
  private _taskId: number;

  /** Currently showing user form */
  private _userForm: DPUserForm = null;

  /** Form data of current form */
  private _formData: DPFormDataResponse = null;

  public get taskId() {
    return this._taskId;
  }

  public get job() {
    return this._job;
  }

  public get form() {
    return this._form;
  }

  public get userForm() {
    return this._userForm;
  }

  public get formData() {
    return this._formData;
  }

  constructor(
    private forms: FormsService,
    private company: CompanyService,
    private dashboard: DashboardService,
    private router: Router,
    private auth: AuthService
  ) {}

  /**
   * Reset application data
   */
  reset() {
    this._job = null;
    this._allForms = [];
    this._form = null;
    this._userForm = null;
    this._formData = null;
  }

  /**
   * Set form object for task. Called from Dashboard when opening task form.
   * @param task Task object passed from Dashboard page
   */
  setTaskForm(task: DPTask) {
    this._taskId = task.taskId;
    const form: DPForm = {
      formID: task.formId,
      formType: '',
      formTypeID: task.formTypeId,
      userForms:
        task.userForms?.map((uf) => ({
          ...uf,
          userStatusID: DPUserStatus.InProgress,
        })) || [],
      name: '',
      sortOrder: 0,
      isTaskForm: true,
    };

    this._allForms = [form];
    this._form = form;
  }

  /**
   * Set job object
   * @param jobID Job ID
   * @returns Observable of job object
   */
  setJob(jobID: number) {
    return this.company.getJobs().pipe(
      map((jobs: DPJob[]) => {
        this._job = jobs.find((job) => job.jobID === jobID) || null;
        return this._job;
      })
    );
  }

  /**
   * Set form object
   * @param formID Form ID
   * @returns Observable of form object
   */
  setForm(formID: number): Observable<DPForm | null> {
    if (!this._job) {
      return of(null);
    }

    return this.forms.getFormsForJob(this._job.jobID).pipe(
      map((forms: DPForm[]) => {
        this._allForms = forms || [];
        const form = this._allForms.find((f) => f.formID === formID);
        this._form = form;
        return form;
      })
    );
  }

  /**
   * Set user form object.
   * @param userFormID User form ID. If not passed, first userForm will be set.
   */
  setUserForm(userFormID?: number) {
    if (!this._form) {
      this._userForm = null;
      return;
    }

    const userForm = !userFormID
      ? this._form.userForms[0]
      : this._form.userForms.find((uf) => uf.userFormID === userFormID);

    if (userForm) {
      this._userForm = userForm;
    } else {
      this._userForm = null;
    }
  }

  /**
   * Apply to a form
   * @param jobID JobID of currently applying form
   * @param formID(optional) Form ID of currently applying form. If not passed, first form of the job will be selected.
   * @returns Form data object
   */
  applyToForm(jobID: number, formID?: number) {
    return this.setJob(jobID).pipe(
      switchMap(() => this.setForm(formID)),
      switchMap(() => {
        // If user forms already exist, skip starting form for job
        if (this._form && this._form.userForms.length) {
          return of(this._form);
        }

        // Start form for job
        return this.forms.startFormForJob(
          formID || this._allForms[0].formID,
          this.job
        );
      }),
      switchMap((form: DPForm) => {
        this._form = form;
        this._userForm = this._form.userForms[0];
        return this.getFormData();
      })
    );
  }

  /**
   * Get current job forms and user forms again from server.
   */
  refreshFormAndUserForm() {
    const user = this.auth.getUserInfo();
    // If job is empty, get dashboard compliance data to refresh tasks.
    if (!this.job) {
      return this.dashboard.getCompliance(true).pipe(
        switchMap((compliance) => {
          if (
            compliance.tasks.length &&
            compliance.tasks.length &&
            user.DriverStatus != 'Y'
          ) {
            const task = compliance.tasks[0];
            if (task.formId) {
              return this.forms.startFormForTask(task.formId, task.taskId).pipe(
                tap((userForms) => {
                  if (userForms.length) {
                    task.userForms = userForms;
                    this.setTaskForm(task);
                    this.setUserForm(userForms[0].userFormID);
                  }
                })
              );
            }

            this._form = null;
            this._userForm = null;
            return of(null);
          }

          this._form = null;
          this._userForm = null;
          return of(null);
        })
      );
    }

    // Get forms for job and reset user form (original job form behavior)
    return this.forms.getFormsForJob(this._job.jobID, true).pipe(
      tap((forms: DPForm[]) => {
        this._allForms = forms || [];
        const form = this._allForms.find((f) => f.formID === this._form.formID);
        this._form = form;

        this.setUserForm(this._userForm.userFormID);
      })
    );
  }

  // Get current user form data
  getFormData() {
    if (!this._userForm || this._form.formTypeID !== DPFormType.Electronic) {
      return of(null);
    }

    return this.forms.getFormData(this._userForm.userFormID).pipe(
      map((response: DPFormDataResponse) => {
        this._formData = response;
        return response;
      })
    );
  }

  /**
   * Get in-progress form
   */
  getInProgressForm(): {
    form: DPForm | null;
    userForm: DPUserForm | null;
  } {
    // Find current in progress form.
    // In-progress form has userForms created and one of them has InProgress status
    const form =
      this._allForms.find(
        (form) =>
          form.userForms.length > 0 &&
          form.userForms.some(
            (userForm) => userForm.userStatusID === DPUserStatus.InProgress
          )
      ) || null;

    if (!form) {
      return { form, userForm: null };
    }

    // Find in-progress user form.
    const userForm =
      form.userForms.find(
        (userForm) => userForm.userStatusID === DPUserStatus.InProgress
      ) || null;

    return { form, userForm };
  }

  /**
   * Get next user form.
   * @returns {Object}
   *   isNextForm - Whether returned form is current form or next form
   */
  getNextUserForm(): {
    isNextForm: boolean;
    form: DPForm | null;
    userForm: DPUserForm | null;
  } {
    if (!this.job && this._form?.isTaskForm) {
      return { isNextForm: true, form: this._form, userForm: this._userForm };
    }

    const form = this._form;
    const userForm = this._userForm;

    if (!form || !userForm) {
      return { isNextForm: false, form, userForm };
    }

    // Current user form index
    let index = form.userForms.findIndex(
      (uf) => uf.userFormID === userForm.userFormID
    );
    index++;

    if (index < form.userForms.length) {
      // If next user form exists, return it
      return { isNextForm: false, form, userForm: form.userForms[index] };
    } else {
      // If next user form does not exist,
      // Get next form and first userForm of next form
      const formIndex = this._allForms.findIndex(
        (f) => f.formID === form.formID
      );
      const nextForm = this._allForms[formIndex + 1] || null;
      const nextUserForm = nextForm ? nextForm.userForms[0] : null;

      return {
        isNextForm: true,
        form: nextForm,
        userForm: nextUserForm || null,
      };
    }
  }

  /**
   * Go to next form.
   */
  gotoNextForm() {
    const next = this.getNextUserForm();
    this.router.navigateByUrl(this.getFormPath(next.form, next.userForm));
  }

  /**
   * Get previous user form
   * @returns {Object}
   *    isPrevForm {boolean} - Whether returned form current form or previous form
   *    form - Job form object
   *    userForm - User form object
   */
  getPrevUserForm(): {
    isPrevForm: boolean;
    form: DPForm | null;
    userForm: DPUserForm | null;
  } {
    const form = this._form;
    const userForm = this._userForm;

    if (!form || !userForm) {
      return { isPrevForm: false, form, userForm };
    }

    // Current user form index
    let index = form.userForms.findIndex(
      (uf) => uf.userFormID === userForm.userFormID
    );
    index--;

    if (index >= 0) {
      // If previous user form exists, return
      return { isPrevForm: false, form, userForm: form.userForms[index] };
    } else {
      // If previous user form does not exist,
      // Find previous job form and return the last userForm of previous job form
      const formIndex = this._allForms.findIndex(
        (f) => f.formID === form.formID
      );
      const prevForm = this._allForms[formIndex - 1] || null;
      const prevUserForm = prevForm
        ? prevForm.userForms[prevForm.userForms.length - 1]
        : null;

      return {
        isPrevForm: true,
        form: prevForm,
        userForm: prevUserForm || null,
      };
    }
  }

  /**
   * Go to previous form
   */
  gotoPrevForm() {
    const prev = this.getPrevUserForm();
    if (prev.userForm) {
      this.router.navigateByUrl(this.getFormPath(prev.form, prev.userForm));
    }
  }

  /**
   * Get form page url
   */
  private getFormPath(form: DPForm | null, userForm: DPUserForm | null) {
    if (!form) {
      return this._job ? '/overview' : '/dashboard';
    }

    const basePath = form.isTaskForm
      ? `/tasks/${this._taskId}/forms`
      : `/jobs/${this._job.jobID}/forms`;

    if (!userForm) {
      return `${basePath}/${form.formID}`;
    }

    return `${basePath}/${form.formID}/${userForm.userFormID}`;
  }
}
